import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { BlockBannerTextBlockProps } from '@ts-blocks/block-banner-text-block/interfaces'
// hooks
import { useDefaultCtaUrl } from '@composables/tools/default-cta-url.hook'
import { useEventsSection } from '@hooks/events/sections/events-section.hook'

const PRIMARY_FALLBACK_TEXT = 'Get started'
const SECONDARY_FALLBACK_TEXT = 'Learn more'

interface BannerCta {
  show: boolean
  text: string
  href: string
}

interface BlockBannerTextBlockHookOptions {
  block: () => BlockBannerTextBlockProps
}

interface BlockBannerTextBlockHookReturn {
  refPrimaryCta: ComputedRef<BannerCta>
  refSecondaryCta: ComputedRef<BannerCta>
  refShowActions: ComputedRef<boolean>
  handleClickPrimary: () => void
  handleClickSecondary: () => void
}

export const useBlockBannerTextBlock = ({ block }: BlockBannerTextBlockHookOptions): BlockBannerTextBlockHookReturn => {
  const refDefaultCtaUrl = useDefaultCtaUrl()
  const { buildCtaHref, trackSectionEvent } = useEventsSection({ sectionName: 'banner-text-block' })

  const buildCta = (
    show: boolean,
    button: BlockBannerTextBlockProps['primaryButton'],
    fallbackText: string,
  ): BannerCta => {
    const text = button?.buttonText || fallbackText
    const base = (button?.isDefaultUrl ?? true) ? refDefaultCtaUrl.value : (button?.buttonLink ?? '#')
    return { show, text, href: buildCtaHref(base, { text }) }
  }

  const refPrimaryCta = computed<BannerCta>(() => (
    buildCta(block().showPrimaryButton !== false, block().primaryButton, PRIMARY_FALLBACK_TEXT)
  ))

  const refSecondaryCta = computed<BannerCta>(() => (
    buildCta(block().showSecondaryButton !== false, block().secondaryButton, SECONDARY_FALLBACK_TEXT)
  ))

  const refShowActions = computed<boolean>(() => refPrimaryCta.value.show || refSecondaryCta.value.show)

  const handleClickPrimary = (): void => {
    trackSectionEvent('Button_click', { name: 'banner_primary', text: refPrimaryCta.value.text, url: refPrimaryCta.value.href })
  }

  const handleClickSecondary = (): void => {
    trackSectionEvent('Button_click', { name: 'banner_secondary', text: refSecondaryCta.value.text, url: refSecondaryCta.value.href })
  }

  return {
    refPrimaryCta,
    refSecondaryCta,
    refShowActions,
    handleClickPrimary,
    handleClickSecondary,
  }
}
